/**
 * WeatherView - Weather display for device
 * Shows current weather and 3-day forecast
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Cloud, CloudRain, Sun, Wind, Droplets, Loader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WeatherView({ senior, onBack }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWeather();
  }, [senior]);

  const fetchWeather = async () => {
    if (!senior?.id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/api/weather/current/${senior.id}?language=de`);
      setWeather(response.data);
      console.log('[Weather] Data loaded:', response.data);
    } catch (err) {
      console.error('[Weather] Error:', err);
      setError('Wetter konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-2xl text-gray-600">Wetter wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Cloud className="w-24 h-24 mx-auto mb-4 text-gray-400" />
          <p className="text-2xl text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchWeather}
            className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xl font-bold"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (!weather) return null;

  const { current, forecast } = weather;

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Current Weather */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Aktuelles Wetter</h2>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="text-8xl">{current.icon}</div>
            <div>
              <div className="text-7xl font-bold text-gray-900">
                {current.temperature}°C
              </div>
              <div className="text-3xl text-gray-600 mt-2">
                {current.description}
              </div>
            </div>
          </div>
          
          <div className="text-right space-y-3">
            <div className="flex items-center gap-3 text-xl text-gray-700">
              <Wind className="w-8 h-8" />
              <span>{current.wind_speed} km/h</span>
            </div>
            <div className="flex items-center gap-3 text-xl text-gray-700">
              <Droplets className="w-8 h-8" />
              <span>{current.humidity}%</span>
            </div>
            <div className="text-xl text-gray-600">
              Gefühlt: {current.feels_like}°C
            </div>
          </div>
        </div>
      </div>

      {/* 3-Day Forecast */}
      <div className="bg-white rounded-3xl shadow-2xl p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">3-Tage Vorhersage</h2>
        
        <div className="grid grid-cols-3 gap-4">
          {forecast.map((day, index) => {
            const date = new Date(day.date);
            const dayName = index === 0 ? 'Heute' : 
                           index === 1 ? 'Morgen' :
                           date.toLocaleDateString('de-DE', { weekday: 'short' });
            
            return (
              <div
                key={day.date}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 text-center"
              >
                <div className="text-2xl font-bold text-gray-900 mb-3">
                  {dayName}
                </div>
                <div className="text-6xl mb-3">{day.icon}</div>
                <div className="text-xl text-gray-700 mb-3">
                  {day.description}
                </div>
                <div className="flex justify-center gap-4 text-lg">
                  <span className="text-red-600 font-bold">{day.temp_max}°</span>
                  <span className="text-blue-600">{day.temp_min}°</span>
                </div>
                {day.precipitation > 0 && (
                  <div className="mt-3 text-blue-600 text-lg">
                    <CloudRain className="w-5 h-5 inline mr-2" />
                    {day.precipitation}mm
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
